import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import MarketingNav from "@/components/site/MarketingNav";
import Footer from "@/components/site/Footer";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Check, ArrowRight, Coins, Home } from "lucide-react";
import { toast } from "sonner";
import { trackViewContent } from "@/lib/analytics";

export default function Pricing() {
  const [plans, setPlans] = useState([]);
  const [packs, setPacks] = useState([]);
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    Promise.all([api.get("/plans"), api.get("/credits/packages")])
      .then(([p, c]) => {
        setPlans(p.data.plans);
        setPacks(c.data.packages);
      })
      .catch(() => toast.error("Failed to load pricing"))
      .finally(() => setLoading(false));
    trackViewContent("pricing_page");
  }, []);

  function handleChoose(code) {
    trackViewContent(code);
    if (!user) {
      localStorage.setItem("draftwave.pending_plan", code);
      navigate("/login");
      return;
    }
    navigate(`/billing?plan=${code}`);
  }

  async function handleBuyPack(code) {
    trackViewContent(code);
    if (!user) {
      navigate("/login");
      return;
    }
    try {
      const r = await api.post("/credits/checkout", { package_code: code, origin_url: window.location.origin });
      if (r.data?.url) window.location.href = r.data.url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Checkout failed");
    }
  }

  return (
    <div className="min-h-screen bg-background" data-testid="pricing-page">
      <MarketingNav />

      <section className="border-b border-border">
        <div className="max-w-[1400px] mx-auto p-8 md:p-16">
          <div className="overline text-muted-foreground">Pricing</div>
          <h1 className="font-serif-display text-5xl md:text-7xl leading-none tracking-tight mt-4">
            Credits are fuel.
            <br />
            <span className="italic text-primary">Subscription is home.</span>
          </h1>
          <p className="mt-6 text-muted-foreground max-w-2xl">
            Start free with 120 credits. Pay-as-you-go with credit packs, or move in with a monthly
            subscription for ongoing volume, priority routing and the full Draftwave experience.
          </p>
        </div>
      </section>

      {/* ---------- SUBSCRIPTION LANE ---------- */}
      <section className="border-b border-border">
        <div className="max-w-[1400px] mx-auto">
          <div className="p-8 md:p-14 border-b border-border">
            <div className="overline text-muted-foreground flex items-center gap-2">
              <Home className="w-3 h-3" /> Subscription · The recurring relationship
            </div>
            <h2 className="font-serif-display text-3xl md:text-5xl leading-none tracking-tight mt-4">
              Want Draftwave in your business every day?
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3">
            {loading ? (
              <div className="col-span-3 p-16 text-center text-muted-foreground overline">
                Loading plans…
              </div>
            ) : (
              plans.map((plan, i) => {
                const isPro = plan.code === "pro";
                const isStudio = plan.code === "studio";
                return (
                  <div
                    key={plan.code}
                    className={`p-10 md:p-12 flex flex-col ${
                      i < 2 ? "md:border-r border-border" : ""
                    } ${i < 2 ? "border-b md:border-b-0 border-border" : ""} ${
                      isStudio ? "bg-foreground text-background" : ""
                    } ${isPro ? "relative" : ""}`}
                    data-testid={`plan-card-${plan.code}`}
                  >
                    {isPro && (
                      <div className="absolute -top-3 left-8 bg-primary text-primary-foreground px-3 py-1 font-mono text-[10px] uppercase tracking-widest">
                        Most popular
                      </div>
                    )}
                    <div className="overline opacity-60">{plan.name}</div>
                    <div className="mt-6 flex items-baseline gap-2">
                      <span className="font-serif-display text-6xl leading-none">
                        {plan.price_display}
                      </span>
                      <span className="text-muted-foreground opacity-70 text-sm">/ month</span>
                    </div>
                    <ul className="mt-8 space-y-3 flex-1">
                      {plan.features.map((f) => (
                        <li key={f} className="flex items-start gap-3 text-sm">
                          <Check className="w-4 h-4 mt-0.5 flex-shrink-0 text-primary" />
                          <span>{f}</span>
                        </li>
                      ))}
                    </ul>
                    <Button
                      onClick={() => handleChoose(plan.code)}
                      className={`mt-10 rounded-none h-12 font-mono uppercase tracking-widest text-xs ${
                        isStudio ? "bg-primary text-primary-foreground hover:bg-primary/90" : ""
                      }`}
                      variant={isPro || isStudio ? "default" : "outline"}
                      data-testid={`plan-cta-${plan.code}`}
                    >
                      Choose {plan.name}
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </Button>
                  </div>
                );
              })
            )}
          </div>
        </div>
      </section>

      {/* ---------- CREDIT PACKS LANE ---------- */}
      <section className="border-b border-border">
        <div className="max-w-[1400px] mx-auto">
          <div className="p-8 md:p-14 border-b border-border">
            <div className="overline text-muted-foreground flex items-center gap-2">
              <Coins className="w-3 h-3" /> Credit packs · Pay for what you create
            </div>
            <h2 className="font-serif-display text-3xl md:text-5xl leading-none tracking-tight mt-4">
              One-time top-ups. <span className="italic text-primary">Credits never expire.</span>
            </h2>
            <p className="mt-4 max-w-2xl text-sm text-muted-foreground">
              Text costs 1 credit. Image costs 5. Video costs 40. Your credits never expire. First
              signup gets 120 free plus 5 more every week.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-4 border-l border-t border-border">
            {packs.map((p, i) => {
              const dark = i === 2;
              return (
                <div
                  key={p.code}
                  className={`p-6 md:p-8 border-r border-b border-border flex flex-col ${
                    dark ? "bg-foreground text-background" : ""
                  }`}
                  data-testid={`credit-pack-${p.code}`}
                >
                  <div className="overline opacity-60">{p.name}</div>
                  <div className="mt-4 flex items-baseline gap-2">
                    <span className="font-serif-display text-4xl md:text-5xl leading-none">
                      {p.price_display}
                    </span>
                    <span className="text-xs opacity-60">one-time</span>
                  </div>
                  <div className="mt-4 font-mono text-sm">
                    {p.credits.toLocaleString()} credits
                    {p.bonus > 0 && <span className="text-primary"> + {p.bonus} bonus</span>}
                  </div>
                  <div className="mt-1 text-xs opacity-70">{p.tagline}</div>
                  <div className="mt-4 flex-1 overline text-[10px] opacity-50 border-t border-current/10 pt-3">
                    {p.audience || "Pay for what you create"}
                  </div>
                  <Button
                    onClick={() => handleBuyPack(p.code)}
                    className={`mt-6 rounded-none h-11 font-mono uppercase tracking-widest text-xs ${
                      dark ? "bg-primary text-primary-foreground hover:bg-primary/90" : ""
                    }`}
                    data-testid={`buy-pack-${p.code}`}
                  >
                    {user ? "Buy pack" : "Sign in to buy"} <ArrowRight className="w-3 h-3 ml-1" />
                  </Button>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <section className="border-b border-border">
        <div className="max-w-[1400px] mx-auto p-10 md:p-16 grid grid-cols-1 md:grid-cols-3 gap-8">
          {[
            { q: "Do credits expire?", a: "Never. Buy once, use forever." },
            { q: "Can I cancel anytime?", a: "Yes. One click in Billing. No calls, no forms." },
            { q: "Do I own what I generate?", a: "Yes. Full commercial rights to every output." },
          ].map((f, i) => (
            <div key={i} data-testid={`faq-${i}`}>
              <div className="overline text-muted-foreground">FAQ 0{i + 1}</div>
              <h4 className="font-serif-display text-2xl md:text-3xl mt-3 leading-tight">{f.q}</h4>
              <p className="mt-3 text-sm text-muted-foreground leading-relaxed">{f.a}</p>
            </div>
          ))}
        </div>
      </section>

      <Footer />
    </div>
  );
}
